import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, ACCESS_TOKEN_KEY } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeUser = useCallback((payload) => {
    if (!payload) return null;
    return {
      id: payload.id,
      email: payload.email,
      name: payload.name,
      role: payload.role,
      subscription_status: payload.subscription_status || 'none',
      stripe_customer_id: payload.stripe_customer_id || '',
      stripe_subscription_id: payload.stripe_subscription_id || '',
      subscription_current_period_end: payload.subscription_current_period_end || '',
      notifications_enabled: Boolean(payload.notifications_enabled),
      notification_preferences: payload.notification_preferences || {},
      avatar: payload.avatar || '',
      bio: payload.bio || '',
    };
  }, []);

  const persistAccessToken = useCallback((token) => {
    if (!token || typeof window === 'undefined') return;
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }, []);

  const clearAccessToken = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  }, []);

  const checkAuth = useCallback(async () => {
    const storedToken = typeof window !== 'undefined'
      ? window.localStorage.getItem(ACCESS_TOKEN_KEY)
      : null;

    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await authAPI.me();
      setUser(normalizeUser(data));
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          const refresh = await authAPI.refresh();
          if (refresh?.data?.token) {
            persistAccessToken(refresh.data.token);
            const { data } = await authAPI.me();
            setUser(normalizeUser(data));
            return;
          }
        } catch (_) {
          // fallthrough to unauthenticated
        }
      }
      clearAccessToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [clearAccessToken, normalizeUser, persistAccessToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    persistAccessToken(data?.token);
    const normalized = normalizeUser(data);
    setUser(normalized);
    return normalized;
  };

  const register = async (email, password, name) => {
    const { data } = await authAPI.register({ email, password, name });
    persistAccessToken(data?.token);
    const normalized = normalizeUser(data);
    setUser(normalized);
    return normalized;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (_) {
      // Ensure local session is cleared even if backend cookie logout fails.
    }
    clearAccessToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function formatApiError(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
